/**
 * ImportDialog - Dialog for importing saves
 */

import type { Component, ChangeEvent } from 'react';
import Dialog from './Dialog';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

interface ImportDialogState {
    status: string;
    isProcessing: boolean;
}

class ImportDialog extends ReactComponent<{ onClose: () => void }, ImportDialogState> {
    private fileInputRef: HTMLInputElement | null = null;

    constructor(props: { onClose: () => void }) {
        super(props);
        this.state = {
            status: '',
            isProcessing: false
        };
    }

    handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Show confirmation dialog before importing
        this.showConfirmDialog(file);
    };

    showConfirmDialog = (file: File): void => {
        const dialogRoot = document.getElementById('confirm-dialog-root');
        if (!dialogRoot) return;

        const React = (window as any).React;
        const ReactDOM = (window as any).ReactDOM;
        const ConfirmDialog = (window as any).ConfirmDialog;
        if (!React || !ReactDOM || !ConfirmDialog) return;

        const handleConfirm = () => {
            // Close confirm dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }

            // Proceed with import
            this.performImport(file);
        };

        const handleCancel = () => {
            // Close confirm dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
            // Reset file input
            if (this.fileInputRef) {
                this.fileInputRef.value = '';
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).confirmDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).confirmDialogRoot = root;
            }
            root.render(React.createElement(ConfirmDialog, {
                title: 'Import Saves',
                message: 'This will overwrite your existing saves. Are you sure you want to continue?',
                onConfirm: handleConfirm,
                onCancel: handleCancel,
                confirmText: 'Import',
                cancelText: 'Cancel',
                confirmButtonClass: 'btn'
            }));
        } else {
            ReactDOM.render(
                React.createElement(ConfirmDialog, {
                    title: 'Import Saves',
                    message: 'This will overwrite your existing saves. Are you sure you want to continue?',
                    onConfirm: handleConfirm,
                    onCancel: handleCancel,
                    confirmText: 'Import',
                    cancelText: 'Cancel',
                    confirmButtonClass: 'btn'
                }),
                dialogRoot
            );
        }
    };

    performImport = (file: File): void => {
        const reader = new FileReader();
        const SavedGames = (window as any).SavedGames;

        if (file.name.toLowerCase().endsWith('.rsv')) {
            this.setState({ status: 'Loading RSV file...', isProcessing: true });
            reader.onload = async (event) => {
                try {
                    const root = await SavedGames.importRSV(event.target?.result as ArrayBuffer);
                    this.setState({ status: 'Importing save data...' });
                    await waitAsync();
                    
                    this.props.onClose();
                    (window as any).handleFileSelect?.(false, '', root);
                } catch (error: any) {
                    this.setState({ 
                        status: 'Error: ' + (error.message || 'Failed to import RSV file'),
                        isProcessing: false 
                    });
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            this.setState({ status: 'Loading JSON file...', isProcessing: true });
            reader.onload = async () => {
                try {
                    await SavedGames.import(JSON.parse(reader.result as string));
                    this.props.onClose();
                    (window as any).GameUI?.showMessage?.('Saves List Imported', { type: 'success', timeout: 3.0 });
                } catch (error: any) {
                    this.setState({ 
                        status: 'Error: ' + (error.message || 'Failed to import JSON file'),
                        isProcessing: false 
                    });
                }
            };
            reader.readAsText(file);
        }
    };

    render(): any {
        const DialogComponent = (window as any).Dialog;
        if (!DialogComponent) return null;

        return React.createElement(DialogComponent, {
            title: 'Import Saves',
            onClose: this.props.onClose,
            actionButtons: React.createElement('button', {
                className: 'btn',
                onClick: this.props.onClose,
                disabled: this.state.isProcessing
            }, 'Dismiss'),
            closeOnBackdropClick: false
        },
            React.createElement('div', { style: { padding: '20px' } },
                React.createElement('p', { style: { marginBottom: '16px', color: 'var(--main-text-color)' } }, 'Choose a .json file of exported saves or .rsv file to import:'),
                React.createElement('input', {
                    ref: (el: HTMLInputElement) => { 
                        this.fileInputRef = el;
                        // Auto-focus file input when dialog opens
                        if (el) {
                            setTimeout(() => {
                                el.click();
                            }, 100);
                        }
                    },
                    type: 'file',
                    accept: '.json,.rsv,.txt',
                    onChange: this.handleFileChange,
                    disabled: this.state.isProcessing,
                    style: {
                        margin: '0',
                        padding: '8px',
                        width: '100%',
                        fontFamily: 'var(--font-game-text)',
                        fontSize: '14px',
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                        background: 'var(--main-bg-color)',
                        color: 'var(--main-text-color)'
                    }
                }),
                this.state.status && React.createElement('div', {
                    className: 'import-menu-status',
                    style: {
                        marginTop: '16px',
                        padding: '12px',
                        backgroundColor: this.state.status.includes('Error') ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                        borderRadius: '4px',
                        color: 'var(--main-text-color)'
                    }
                }, this.state.status)
            )
        );
    }
}

(window as any).ImportDialog = ImportDialog;
export default ImportDialog;
